import _ from 'lodash';
import Papa from 'papaparse';
import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {RouteService} from '../../../../services/routeService';
import {NotificationService} from '../../../../services/notificationService';
import {DICTIONARY} from '../../../../dictionary';
import {GeneralService} from '../../../../services/generalService';
import util from 'util';

@Component({
    selector: 'threat-simulation-quiz-creation-wizard',
    templateUrl: './threat-simulation-quiz-creation-wizard.component.html',
    styleUrls: ['./threat-simulation-quiz-creation-wizard.component.scss']
})
export class ThreatSimulationQuizCreationWizardComponent implements OnInit {

    constructor(private rs: RouteService,
                private ns: NotificationService,
                public gs: GeneralService) {}

    @Input() initialData;
    @Input() isEditMode = false;
    @Output() doneCb = new EventEmitter();
    @Output() cancelCb = new EventEmitter();

    dic = DICTIONARY;

    quizCreationSteps = {
        instructions: 'Instructions',
        quizInfo: 'Quiz Info',
        multipleChoiceQuestions: 'Multiple-Choice Questions',
        summary: 'Summary & Execute'
    };

    currentStep = this.quizCreationSteps.instructions;
    createInProcess = false;
    error = false;
    questionCounter = 0;
    previewMaterialObj;
	importQuizQuestionsPopup;

    optionLetters = ['A', 'B', 'C', 'D'];

    defaultEmailTestOptions = [
        'Phishing - This is likely a scam',
        'Legitimate - This appears to be a real email'
    ];

    data = {
        name: '',
        description: '',
        quiz_config: {
            instructions: '',
			enable_questions_count: false,
            questions_count: 7,
			enable_passing_score: false,
			passing_score: 50
        },
        multipleChoiceQuestions: [this.newMCQuestion()],
        emailExampleQuestions: []
    };

    ngOnInit() {
        if (this.initialData) {
            this.currentStep = this.quizCreationSteps.quizInfo;
            const storedQuestions = this.initialData.quiz?.questions || [];
            this.data.name = this.initialData.name || '';
            this.data.description = this.initialData.description || '';
            this.data.quiz_config.instructions = this.initialData.quiz?.config?.instructions || '';
            this.data.quiz_config.questions_count = this.initialData.quiz?.config?.questions_count;
			this.data.quiz_config.enable_questions_count = !!this.initialData.quiz?.config?.questions_count;
			this.data.quiz_config.passing_score = this.initialData.quiz?.config?.passing_score ?? 50;
			this.data.quiz_config.enable_passing_score = !!this.initialData.quiz?.config?.passing_score;
			this.data.multipleChoiceQuestions = storedQuestions.map(q => ({
                id: q.id,
                type: 'multiple-choice',
                question: q.question,
                options: [...q.options],
                correctIndex: 0,
                explanation: q.explanation || ''
            }));
            if (!this.data.multipleChoiceQuestions.length) {
                this.data.multipleChoiceQuestions = [this.newMCQuestion()];
            }
            // CC: set counter above existing IDs to avoid collisions when adding new questions
            this.questionCounter = Math.max(...storedQuestions.map(q => Number(q.id) || 0), 0);
        }
    }

    trackByIndex(index) {
        return index;
    }

    newMCQuestion() {
        return {
            id: ++this.questionCounter,
            type: 'multiple-choice',
            question: '',
            options: ['', '', '', ''],
            correctIndex: 0,
            explanation: ''
        };
    }

    newEmailQuestion() {
        return {
            id: ++this.questionCounter,
            type: 'email-test',
            question: '',
            useDefaultOptions: true,
            options: ['', ''],
            explanation: '',
            emailData: {
                from: '',
                subject: '',
                body: '',
                timestamp: '',
                newAttachment: '',
                attachments: [],
                newIndicator: '',
                suspicious_indicators: []
            }
        };
    }

    nextStep() {
        this.error = false;

        switch (this.currentStep) {
            case this.quizCreationSteps.instructions:
                this.currentStep = this.quizCreationSteps.quizInfo;
                break;

            case this.quizCreationSteps.quizInfo:
                if (!this.data.name.trim()) {
                    this.ns.showWarnMessage(util.format(this.dic.ERRORS.cannotBeEmpty, 'Quiz name'));
                    this.error = true;
                    return;
                }
                if (this.data.quiz_config.enable_questions_count && (!this.data.quiz_config.questions_count || this.data.quiz_config.questions_count < 1)) {
                    this.ns.showWarnMessage(util.format(this.dic.ERRORS.EnterValidX, 'Questions per session'));
                    this.error = true;
                    return;
                }
                if (this.data.quiz_config.enable_passing_score && (!this.data.quiz_config.passing_score || this.data.quiz_config.passing_score < 1)) {
                    this.ns.showWarnMessage(util.format(this.dic.ERRORS.EnterValidX, 'Passing score'));
                    this.error = true;
                    return;
                }
                this.currentStep = this.quizCreationSteps.multipleChoiceQuestions;
                break;

            case this.quizCreationSteps.multipleChoiceQuestions:
                for (const q of this.data.multipleChoiceQuestions) {
                    if (!q.question.trim()) {
                        this.ns.showWarnMessage(util.format(this.dic.ERRORS.cannotBeEmpty, 'Question text'));
                        this.error = true;
                        return;
                    }
                    if (q.options.some(o => !o.trim())) {
                        this.ns.showWarnMessage(util.format(this.dic.ERRORS.cannotBeEmpty, 'Answer option'));
                        this.error = true;
                        return;
                    }
                    const trimmed = q.options.map(option => option.trim().toLowerCase());
                    if (trimmed.length !== new Set(trimmed).size) {
                        this.ns.showWarnMessage('Answer options must be unique within each question');
                        this.error = true;
                        return;
                    }
                }
                // CC: compute preview data when entering summary
                this.previewMaterialObj = this.buildPreviewMaterialObj();
                this.currentStep = this.quizCreationSteps.summary;
                break;

            default:
                return;
        }
    }

    backStep() {
        this.error = false;

        switch (this.currentStep) {
            case this.quizCreationSteps.quizInfo:
                this.currentStep = this.quizCreationSteps.instructions;
                break;
            case this.quizCreationSteps.multipleChoiceQuestions:
                this.currentStep = this.quizCreationSteps.quizInfo;
                break;
            case this.quizCreationSteps.summary:
                this.currentStep = this.quizCreationSteps.multipleChoiceQuestions;
                break;
            default:
                return;
        }
    }

    addMCQuestion() {
        this.data.multipleChoiceQuestions.push(this.newMCQuestion());
    }

    removeMCQuestion(index) {
        this.data.multipleChoiceQuestions.splice(index, 1);
    }

    duplicateMCQuestion(index) {
        const original = this.data.multipleChoiceQuestions[index];
        const copy = {
            ...original,
            id: ++this.questionCounter,
            options: [...original.options]
        };
        this.data.multipleChoiceQuestions.splice(index + 1, 0, copy);
    }

    addOption(question) {
        if (question.options.length >= 4) {
            return;
        }
        question.options.push('');
    }

    removeOption(question, index) {
        if (question.options.length <= 2) {
            return;
        }
        question.options.splice(index, 1);
        if (question.correctIndex >= question.options.length) {
            question.correctIndex = 0;
        }
    }

    buildPreviewMaterialObj() {
		const materialPreviewObj:any = {
			name: this.data.name || 'Quiz Preview',
			description: this.data.description,
			quiz: {
				questions: this.buildQuestionsPayload(),
				config: {
					instructions: this.data.quiz_config.instructions.trim() || undefined,
				}
			}
		};

		if (this.data.quiz_config.enable_questions_count) {
			materialPreviewObj.quiz.config.questions_count = this.data.quiz_config.questions_count;
		}
		if (this.data.quiz_config.enable_passing_score) {
			materialPreviewObj.quiz.config.passing_score = this.data.quiz_config.passing_score;
		}

        return materialPreviewObj;
    }

    buildQuestionsPayload() {
        const mcQuestions = this.data.multipleChoiceQuestions.map(q => {
            // CC: move correct answer to index 0 per convention
            const options = q.options.map(o => o.trim());
            const correct = options.splice(q.correctIndex, 1)[0];
            options.unshift(correct);
            return {
                id: q.id,
                type: 'multiple-choice',
                question: q.question.trim(),
                options,
                ...(q.explanation.trim() && {explanation: q.explanation.trim()})
            };
        });

        const emailQuestions = this.data.emailExampleQuestions.map(q => {
            const options = q.useDefaultOptions
                ? [...this.defaultEmailTestOptions]
                : [q.options[0].trim(), q.options[1].trim()];
            return {
                id: q.id,
                type: 'email-test',
                question: q.question.trim() || 'Is this email legitimate or a phishing attack?',
                options,
                ...(q.explanation.trim() && {explanation: q.explanation.trim()}),
                emailData: {
                    from: q.emailData.from.trim(),
                    subject: q.emailData.subject.trim(),
                    body: q.emailData.body.trim(),
                    timestamp: q.emailData.timestamp.trim(),
                    ...(q.emailData.attachments.length && {attachments: q.emailData.attachments}),
                    ...(q.emailData.suspicious_indicators.length && {suspicious_indicators: q.emailData.suspicious_indicators})
                }
            };
        });

        return [...mcQuestions, ...emailQuestions];
    }

    openCsvImportPopup() {
        this.importQuizQuestionsPopup = {
            show: true,
            importMode: 'append',
            file: null,
            hasExistingQuestions: this.data.multipleChoiceQuestions.some(question => question.question.trim())
        };
    }


    onCsvFileSelected(event) {
        this.importQuizQuestionsPopup.file = event.target.files?.[0] || null;
    }

    importCsvQuestions() {
        if (!this.importQuizQuestionsPopup.file) {
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            // CC: taken from generalService.ts -> readCsv (Papa.parse usage)
            const parsed = Papa.parse(reader.result.toString(), {skipEmptyLines: true});
            const lines = parsed.data as string[][];

            if (!lines.length) {
                this.ns.showErrorMessage('The file is empty.');
                return;
            }

            const importedQuestions = [];

            for (let rowIndex = 0; rowIndex < lines.length; rowIndex++) {
                const columns = lines[rowIndex].map(column => column.trim());
                const rowNum = rowIndex + 1;

                if (columns.length < 3 || columns.length > 7) {
                    this.ns.showErrorMessage(`Row ${rowNum} has an unexpected number of columns (${columns.length}). Expected between 3 and 7.`);
                    return;
                }

                const questionText = columns[0];
                const correctAnswer = columns[1];
                const options = columns.slice(1, 5).filter(option => option);
                const explanation = columns[5] || '';

                if (!questionText) {
                    this.ns.showErrorMessage(`Row ${rowNum} is missing a question.`);
                    return;
                }

                if (!correctAnswer) {
                    this.ns.showErrorMessage(`Row ${rowNum} is missing a correct answer.`);
                    return;
                }

                if (options.length < 2) {
                    this.ns.showErrorMessage(`Row ${rowNum} must have at least 2 answer options.`);
                    return;
                }

                const hasDuplicateOptions = options.some((option, index) => options.indexOf(option) !== index);
                if (hasDuplicateOptions) {
                    this.ns.showErrorMessage(`Row ${rowNum} contains duplicate answer options.`);
                    return;
                }

                const shuffledOptions = _.shuffle(options);
                const correctIndex = shuffledOptions.indexOf(options[0]);

                importedQuestions.push({
                    id: ++this.questionCounter,
                    type: 'multiple-choice',
                    question: questionText,
                    options: shuffledOptions,
                    correctIndex,
                    explanation
                });
            }

            if (this.importQuizQuestionsPopup.importMode === 'replace') {
                this.data.multipleChoiceQuestions = importedQuestions;
            } else {
                this.data.multipleChoiceQuestions.push(...importedQuestions);
            }
            this.data.multipleChoiceQuestions = this.data.multipleChoiceQuestions.filter(question => question.question.trim());

            this.importQuizQuestionsPopup = null;
        };
        reader.readAsText(this.importQuizQuestionsPopup.file);
    }

    createQuiz() {
        const questions = this.buildQuestionsPayload();
        if (!questions.length) {
            this.ns.showWarnMessage('Please add at least one question');
            return;
        }

        const payload:any = {
            name: this.data.name.trim(),
            description: this.data.description.trim(),
            type: 'quiz',
            quiz: {
                questions,
                config: {}
            }
        };

		if (this.data.quiz_config.instructions.trim()) {
			payload.quiz.config.instructions = this.data.quiz_config.instructions.trim();
		}
		if (this.data.quiz_config.enable_questions_count) {
			payload.quiz.config.questions_count = this.data.quiz_config.questions_count;
		}
		if (this.data.quiz_config.enable_passing_score) {
			payload.quiz.config.passing_score = this.data.quiz_config.passing_score;
		}

        this.createInProcess = true;

        if (this.isEditMode) {
            const updatePayload = {type: 'quiz', value: {name: payload.name, description: payload.description, quiz: payload.quiz}};
            this.rs.updateThreatSimulationMaterial(this.initialData._id, updatePayload).then(() => {
                this.createInProcess = false;
                this.doneCb.emit({...this.initialData, name: payload.name, description: payload.description, quiz: payload.quiz});
            }, () => {
                this.createInProcess = false;
            });
        } else {
            this.rs.addThreatSimulationMaterialQuiz(payload).then((res) => {
                this.createInProcess = false;
                this.doneCb.emit(res.materialObj);
            }, () => {
                this.createInProcess = false;
            });
        }
    }
}

